import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useDarkMode } from './DarkModeContext';
import { previewBlob, saveBlob } from './downloadFile';
import {
  FPQuestionRow, FPRecordedAnswers, FPRecordedValue, FPSubmitButton,
  fpColors, fmtFpDate, FP_NAVY, FP_GOLD, fpRecordFor, fpStepRefusal, fpSamePersonNotes,
} from './FPAssessmentShared';

const API = '/api';

// FPFRONTEND stages 2-5: the review chain, one section on the Validation page.
//
// WHICH STAGE A VIEWER SEES IS DERIVED FROM THE RECORD'S OWN STATUS, mirroring the backend's
// gates exactly rather than guessing:
//   submitted        -> the MANAGER answers their three questions
//   manager_reviewed -> HR answers their one question
//   hr_reviewed      -> COMPLIANCE records one of two outcomes
//   investigation    -> COMPLIANCE records the follow up (stage 5)
//   completed        -> nothing actionable; the record is read only for anyone who can see it
// A viewer whose role does not own the CURRENT stage gets the read-only view and no controls, so
// each role only ever acts on the stage that is genuinely theirs (item 7).
export const FP_STAGE_FOR_STATUS = {
  submitted: 'manager',
  manager_reviewed: 'hr',
  hr_reviewed: 'compliance',
  investigation: 'followup',
};

/** Does this viewer own the stage the record is currently sitting at? FPSTEPOWNER: the server
 *  decides, through the record's can_act (the one person allowed under fp_step_owner); this role
 *  based guess is used only for a record that carries no can_act. */
export function fpViewerOwnsStage(stage, { role, isManagerOfSubject }) {
  if (stage === 'manager') return isManagerOfSubject || role === 'coo' || role === 'admin';
  if (stage === 'hr') {
    // HRPROFILE: anything HR can do, the COO can also do, so the COO owns the HR step as well,
    // whether or not an hr account exists (the backend's _fp_hr_reviewer applies the same rule).
    return role === 'hr' || role === 'admin' || role === 'coo';
  }
  if (stage === 'compliance' || stage === 'followup') return role === 'compliance';
  return false;
}

/** FPSTEPOWNER: the record's own can_act when the server gives it, else the role based guess. */
export function fpRecordOwned(record, stage, ctx) {
  if (record && typeof record.can_act === 'boolean') return record.can_act;
  return fpViewerOwnsStage(stage, ctx);
}

// FPSTEPOWNER: the note shown to anyone who sees the assessment while another person's step is open.
export const FP_WAITING_KEY = {
  manager: 'fp.waitingManager',
  hr: 'fp.waitingHr',
  compliance: 'fp.waitingCompliance',
  followup: 'fp.waitingFollowup',
};

// FPFIX: `period` names the assessment opened from the Validation list. The server returns every
// period on file, so without it the newest is shown.
// FPFIX2: a supporting document recorded on the assessment (HR's, or the follow-up's), viewed or
// downloaded through GET /fp-assessment/{employee_id}/document/{document_id}, which admits exactly
// the people who may read the assessment. Rendered only when the record names a document.
function FPAttachment({ employeeId, documentId, label, isDarkMode }) {
  const { t } = useTranslation();
  const c = fpColors(isDarkMode);
  const [failed, setFailed] = useState(false);
  const fetchDoc = (then) => {
    setFailed(false);
    axios.get(`${API}/fp-assessment/${employeeId}/document/${documentId}`, { responseType: 'blob' })
      .then(then)
      .catch(() => setFailed(true));
  };
  const btn = {
    background: 'transparent', color: FP_GOLD, border: `1px solid ${FP_GOLD}`, borderRadius: '6px',
    padding: '4px 10px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer',
  };
  return (
    <div data-testid="fp-attachment" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
      <span style={{ fontSize: '12.5px', color: c.text }}>{label}</span>
      <button type="button" style={btn} onClick={() => fetchDoc(previewBlob)}>{t('fp.view')}</button>
      <button type="button" style={btn} onClick={() => fetchDoc(saveBlob)}>{t('fp.download')}</button>
      {failed && <span role="alert" style={{ fontSize: '12.5px', color: '#A63A3A' }}>{t('fp.openFailed')}</span>}
    </div>
  );
}

export default function FPReviewSection({ employeeId, period, role, isManagerOfSubject, hrAccountExists }) {
  const { t } = useTranslation();
  const { isDarkMode } = useDarkMode();
  const c = fpColors(isDarkMode);

  const [questions, setQuestions] = useState({ manager_questions: [], hr_question: null, compliance_outcomes: [], followup_outcomes: [] });
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Stage-local form state.
  const [mgrAnswers, setMgrAnswers] = useState({});
  const [hrChoice, setHrChoice] = useState('');
  const [hrCommentary, setHrCommentary] = useState('');
  const [hrFile, setHrFile] = useState(null);
  const [compOutcome, setCompOutcome] = useState('');
  const [investigation, setInvestigation] = useState('');
  const [followFile, setFollowFile] = useState(null);
  const [followOutcome, setFollowOutcome] = useState('');

  const load = useCallback(() => {
    if (!employeeId) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/fp-assessment/questions/all`),
      axios.get(`${API}/fp-assessment/${employeeId}`),
    ])
      .then(([q, r]) => { setQuestions(q.data || {}); setRecord(fpRecordFor(r.data, period)); setLoadFailed(false); })
      .catch(() => { setRecord(null); setLoadFailed(true); })
      .finally(() => setLoading(false));
  }, [employeeId, period]);

  useEffect(() => { load(); }, [load]);

  const status = record?.status;
  const stage = FP_STAGE_FOR_STATUS[status] || null;
  const owns = stage
    ? fpRecordOwned(record, stage, { role, isManagerOfSubject, hrAccountExists })
    : false;

  const fail = (err, fallback) => {
    const d = err.response?.data?.detail;
    setError(fpStepRefusal(d, t) || (typeof d === 'string' ? d : fallback));
  };

  /** Uploads a file through the portal's ONE existing document endpoint and returns its
   *  document_id, which is the only thing the F&P endpoints accept. No new upload path. */
  const uploadDoc = async (file, { fpFollowup = false } = {}) => {
    const fd = new FormData();
    fd.append('employee_id', employeeId);
    fd.append('document_type', 'other');
    // FPFIX: marks the follow-up document, the one upload compliance is allowed to make here.
    if (fpFollowup) fd.append('fp_followup', '1');
    fd.append('file', file);
    const res = await axios.post(`${API}/documents/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data?.document_id || null;
  };

  const submitManager = () => {
    const qs = questions.manager_questions || [];
    if (qs.some(q => !mgrAnswers[q.question_id]?.answer)) {
      setError('Answer all three questions before submitting.');
      return;
    }
    setBusy(true); setError('');
    axios.post(`${API}/fp-assessment/${employeeId}/manager-review`, {
      decision: 'Confirmed',
      responses: qs.map(q => ({
        question_id: q.question_id,
        answer: mgrAnswers[q.question_id].answer,
        notes: (mgrAnswers[q.question_id].notes || '').trim() || null,
      })),
    })
      .then(res => setRecord(res.data))
      .catch(err => fail(err, 'Could not submit the manager review.'))
      .finally(() => setBusy(false));
  };

  const submitHr = async () => {
    if (!hrChoice) { setError('Select Yes or No.'); return; }
    // The commentary is mandatory ONLY on Yes - the same rule the backend enforces.
    if (hrChoice === 'Yes' && !hrCommentary.trim()) {
      setError('A commentary is required when issues have come to your attention.');
      return;
    }
    setBusy(true); setError('');
    try {
      const documentId = hrFile ? await uploadDoc(hrFile) : null;
      const res = await axios.post(`${API}/fp-assessment/${employeeId}/hr-review`, {
        choice: hrChoice,
        commentary: hrCommentary.trim() || null,
        document_id: documentId,
      });
      setRecord(res.data);
    } catch (err) {
      fail(err, 'Could not submit the HR review.');
    } finally { setBusy(false); }
  };

  const submitCompliance = () => {
    if (!compOutcome) { setError('Select an outcome.'); return; }
    setBusy(true); setError('');
    axios.post(`${API}/fp-assessment/${employeeId}/compliance-outcome`, { outcome: compOutcome })
      // The response carries the new status, which is what routes through to stage 5 when the
      // investigation outcome was chosen - no client-side guess about where it goes next.
      .then(res => { setRecord(res.data); setCompOutcome(''); })
      .catch(err => fail(err, 'Could not record the outcome.'))
      .finally(() => setBusy(false));
  };

  const submitFollowup = async () => {
    if (!investigation.trim()) { setError('Describe the investigation performed.'); return; }
    if (!followOutcome) { setError('Select a final outcome.'); return; }
    setBusy(true); setError('');
    try {
      const documentId = followFile ? await uploadDoc(followFile, { fpFollowup: true }) : null;
      const res = await axios.post(`${API}/fp-assessment/${employeeId}/followup`, {
        investigation: investigation.trim(),
        document_id: documentId,
        outcome: followOutcome,
      });
      setRecord(res.data);
    } catch (err) {
      fail(err, 'Could not record the follow up.');
    } finally { setBusy(false); }
  };

  if (!employeeId) return null;
  if (loading) return <div style={{ fontSize: '13px', color: c.muted }}>Loading…</div>;
  if (loadFailed) {
    return (
      <div style={{ fontSize: '13px', color: '#A63A3A' }}>
        Could not load this assessment. Please refresh - do not assume there is nothing outstanding.
      </div>
    );
  }
  if (!record) return null;

  const hrQ = questions.hr_question;
  const radio = (checked) => ({
    background: checked ? FP_NAVY : 'transparent',
    color: checked ? '#fff' : c.text,
    border: checked ? `1px solid ${FP_NAVY}` : c.border,
    borderRadius: '6px', padding: '8px 14px', fontSize: '13px',
    fontWeight: 600, cursor: 'pointer', textAlign: 'left',
  });

  return (
    <section style={{ marginBottom: '28px' }}>
      <div style={{ fontSize: '15px', fontWeight: 700, color: c.text, marginBottom: '4px' }}>
        {t('fp.title')}
      </div>
      <div style={{ height: '3px', width: '36px', background: FP_GOLD, borderRadius: '2px', marginBottom: '12px' }} />

      {/* FPTWOROLES: one person completed two stages, as no one else held the role. */}
      {fpSamePersonNotes(record, t).map(note => (
        <div key={note} data-testid="fp-same-person" style={{ fontSize: '12.5px', color: c.muted, marginBottom: '10px' }}>
          {note}
        </div>
      ))}

      {/* PRIOR STAGES, READ ONLY. Each block renders only once its stage has been recorded, so a
          viewer sees exactly what has happened so far and nothing speculative. Every recorded
          answer comes from the server's stored question_text (item 8). */}
      <FPRecordedAnswers
        title="Employee responses"
        highlightUnexpected
        rows={record.questionnaire_responses}
        byline={record.submitted_at ? `Submitted ${fmtFpDate(record.submitted_at)}` : null}
        isDarkMode={isDarkMode}
      />

      {(record.manager_responses || []).length > 0 && (
        <FPRecordedAnswers
          title="Manager responses"
          rows={record.manager_responses}
          byline={record.manager_reviewed_at ? `Reviewed ${fmtFpDate(record.manager_reviewed_at)}` : null}
          isDarkMode={isDarkMode}
        />
      )}

      <FPRecordedValue
        title="HR review"
        value={record.hr_choice}
        detail={record.hr_commentary}
        byline={record.hr_reviewed_at ? `Reviewed ${fmtFpDate(record.hr_reviewed_at)}` : null}
        isDarkMode={isDarkMode}
      />
      {record.hr_document_id && (
        <FPAttachment employeeId={employeeId} documentId={record.hr_document_id}
                      label={t('fp.attachmentHr')} isDarkMode={isDarkMode} />
      )}

      <FPRecordedValue
        title="Compliance outcome"
        value={record.compliance_outcome_label}
        byline={record.reviewed_at ? `Recorded ${fmtFpDate(record.reviewed_at)}` : null}
        isDarkMode={isDarkMode}
      />

      <FPRecordedValue
        title="F&P follow up"
        value={record.followup_outcome_label}
        detail={record.followup_investigation}
        byline={record.followup_recorded_at ? `Recorded ${fmtFpDate(record.followup_recorded_at)}` : null}
        isDarkMode={isDarkMode}
      />
      {record.followup_document_id && (
        <FPAttachment employeeId={employeeId} documentId={record.followup_document_id}
                      label={t('fp.attachmentFollowup')} isDarkMode={isDarkMode} />
      )}

      {status === 'completed' && (
        <div style={{ fontSize: '13px', color: c.muted, marginTop: '4px' }}>
          This assessment is complete. No further action is required.
        </div>
      )}

      {/* THE ACTIONABLE STAGE, shown only to the role that owns it. */}
      {stage && !owns && (
        <div style={{ fontSize: '13px', color: c.muted, marginTop: '4px' }}>
          {t(FP_WAITING_KEY[stage])}
        </div>
      )}

      {stage && owns && (
        <div style={{ marginTop: '6px', paddingTop: '14px', borderTop: c.rowBorder }}>
          {error && (
            <div style={{ fontSize: '12.5px', color: '#A63A3A', marginBottom: '10px' }}>{error}</div>
          )}

          {stage === 'manager' && (
            <>
              <div style={{ fontSize: '13px', color: c.muted, marginBottom: '10px' }}>
                Your review. Notes are optional on each question.
              </div>
              {(questions.manager_questions || []).map((q, i) => (
                <FPQuestionRow
                  key={q.question_id}
                  index={i + 1}
                  questionId={q.question_id}
                  questionText={q.question_text}
                  value={mgrAnswers[q.question_id]?.answer}
                  notes={mgrAnswers[q.question_id]?.notes}
                  onChange={patch => setMgrAnswers(p => ({ ...p, [q.question_id]: { ...(p[q.question_id] || {}), ...patch } }))}
                  isDarkMode={isDarkMode}
                />
              ))}
              <FPSubmitButton onClick={submitManager} busy={busy}>Submit review</FPSubmitButton>
            </>
          )}

          {stage === 'hr' && hrQ && (
            <>
              <div style={{ fontSize: '12.5px', color: c.text, marginBottom: '8px' }}>{hrQ.question_text}</div>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                {['Yes', 'No'].map(opt => (
                  <button
                    key={opt}
                    type="button"
                    aria-pressed={hrChoice === opt}
                    aria-label={`hr ${opt}`}
                    onClick={() => { setHrChoice(opt); setError(''); }}
                    style={radio(hrChoice === opt)}
                  >
                    {opt}
                  </button>
                ))}
              </div>
              {/* The commentary and the upload appear ONLY on Yes - the upload is optional even
                  then, and the commentary is the one mandatory free text in this chain. */}
              {hrChoice === 'Yes' && (
                <>
                  <textarea
                    value={hrCommentary}
                    onChange={e => { setHrCommentary(e.target.value); setError(''); }}
                    maxLength={4000}
                    rows={3}
                    placeholder="Commentary (required)"
                    aria-label="HR commentary"
                    style={{
                      width: '100%', boxSizing: 'border-box', fontSize: '13px', padding: '7px 9px',
                      borderRadius: '6px', border: c.inputBorder, background: c.inputBg,
                      color: c.text, fontFamily: 'inherit', resize: 'vertical', marginBottom: '10px',
                    }}
                  />
                  <div style={{ marginBottom: '10px' }}>
                    <input
                      type="file"
                      aria-label="HR supporting document"
                      onChange={e => setHrFile(e.target.files[0] || null)}
                      style={{ fontSize: '13px', color: c.text }}
                    />
                    <div style={{ fontSize: '12px', color: c.muted, marginTop: '3px' }}>
                      Supporting document (optional)
                    </div>
                  </div>
                </>
              )}
              <FPSubmitButton onClick={submitHr} busy={busy}>Submit HR review</FPSubmitButton>
            </>
          )}

          {stage === 'compliance' && (
            <>
              <div style={{ fontSize: '13px', color: c.muted, marginBottom: '10px' }}>
                Record the outcome of your review.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                {(questions.compliance_outcomes || []).map(o => (
                  <button
                    key={o.value}
                    type="button"
                    aria-pressed={compOutcome === o.value}
                    aria-label={o.value}
                    onClick={() => { setCompOutcome(o.value); setError(''); }}
                    style={radio(compOutcome === o.value)}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              <FPSubmitButton onClick={submitCompliance} busy={busy}>Record outcome</FPSubmitButton>
            </>
          )}

          {stage === 'followup' && (
            <>
              <div style={{ fontSize: '13px', color: c.muted, marginBottom: '10px' }}>
                An investigation is underway. Record what was performed and the final outcome.
              </div>
              <textarea
                value={investigation}
                onChange={e => { setInvestigation(e.target.value); setError(''); }}
                maxLength={8000}
                rows={4}
                placeholder="Describe the investigation performed (required)"
                aria-label="Investigation performed"
                style={{
                  width: '100%', boxSizing: 'border-box', fontSize: '13px', padding: '7px 9px',
                  borderRadius: '6px', border: c.inputBorder, background: c.inputBg,
                  color: c.text, fontFamily: 'inherit', resize: 'vertical', marginBottom: '10px',
                }}
              />
              <div style={{ marginBottom: '12px' }}>
                <input
                  type="file"
                  aria-label="Follow up supporting document"
                  onChange={e => setFollowFile(e.target.files[0] || null)}
                  style={{ fontSize: '13px', color: c.text }}
                />
                <div style={{ fontSize: '12px', color: c.muted, marginTop: '3px' }}>
                  Supporting document (optional)
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                {(questions.followup_outcomes || []).map(o => (
                  <button
                    key={o.value}
                    type="button"
                    aria-pressed={followOutcome === o.value}
                    aria-label={o.value}
                    onClick={() => { setFollowOutcome(o.value); setError(''); }}
                    style={radio(followOutcome === o.value)}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              <FPSubmitButton onClick={submitFollowup} busy={busy}>Record follow up</FPSubmitButton>
            </>
          )}
        </div>
      )}
    </section>
  );
}
